import { effect, inject, Injectable, signal } from '@angular/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { AuthStore } from '@staffler/app/core';
import { NotificationService } from '@staffler/app/modules/notifications/notifications.service';
import { opensShiftDetail } from '@staffler/app/modules/notifications/models/notifications.model';
import { TabRoutePath } from '@staffler/app/modules/tabs/tabs.routes';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  readonly #notificationService = inject(NotificationService);
  readonly #authStore = inject(AuthStore);
  readonly #router = inject(Router);
  readonly #currentUser = this.#authStore.currentUser;
  readonly #deviceToken = signal<string | null>(null);
  #listeners: PluginListenerHandle[] = [];
  #initialized = false;
  #subscribedKey: string | null = null;

  constructor() {
    effect(() => {
      const token = this.#deviceToken();
      const employeeId = this.#currentUser()?.employeeId;
      if (!token || !employeeId) return;

      const key = `${employeeId}:${token}`;
      if (key === this.#subscribedKey) return;
      this.#subscribedKey = key;

      this.#notificationService.subscribe(employeeId, undefined, token).subscribe({
        error: err => {
          console.error('[PushNotificationService] subscribe failed:', err);
          if (this.#subscribedKey === key) this.#subscribedKey = null;
        },
      });
    });
  }

  async init(): Promise<void> {
    if (this.#initialized) return;
    if (!Capacitor.isNativePlatform()) return;

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return;

    this.#initialized = true;

    const registrationListener = await PushNotifications.addListener('registration', token =>
      this.#deviceToken.set(token.value)
    );

    this.#listeners.push(registrationListener);

    this.#listeners.push(
      await PushNotifications.addListener('registrationError', error => {
        console.error(error);
      })
    );

    this.#listeners.push(
      await PushNotifications.addListener('pushNotificationReceived', async n => {
        await this.#notificationService.addFromFcm({
          id: n.id,
          title: n.title ?? '',
          body: n.body ?? '',
          data: n.data,
        });
      })
    );

    this.#listeners.push(
      await PushNotifications.addListener('pushNotificationActionPerformed', async a => {
        const n = a.notification;

        await this.#notificationService.addFromFcm({
          id: n.id,
          title: n.title ?? '',
          body: n.body ?? '',
          data: n.data,
        });

        if (n.id) await this.#notificationService.markAsRead(n.id);

        this.#navigateToNotification(n.data);
      })
    );

    await PushNotifications.register();
  }

  #navigateToNotification(
    data: { notificationType?: string; contractId?: string } | undefined
  ): void {
    const target =
      opensShiftDetail(data?.notificationType) && data?.contractId
        ? ['/tabs', TabRoutePath.SCHEDULE, 'detail', data.contractId]
        : ['/tabs', TabRoutePath.NOTIFICATIONS];

    if (this.#router.navigated) {
      this.#router.navigate(target);
      return;
    }

    this.#router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => this.#router.navigate(target));
  }

  async destroy(): Promise<void> {
    const user = this.#currentUser();

    await Promise.all(this.#listeners.map(l => l.remove()));
    this.#listeners = [];
    this.#initialized = false;

    if (user) {
      try {
        await firstValueFrom(this.#notificationService.unsubscribe(user.employeeId as string));
      } catch (err) {
        console.error('[PushNotificationService] unsubscribe failed:', err);
      }
    }

    this.#subscribedKey = null;
    this.#deviceToken.set(null);
  }
}
